/**
 * VideoUrlService - Direct HLS URL generation for lightweight previews
 *
 * Generates direct CDN URLs for video content bypassing DotPlayer API overhead.
 * Used exclusively for preview experiences in ThumbnailHover and PDPModal.
 *
 * URL Pattern: https://chillfreedspmedia.cachefly.net/files/company/{COMPANY_ID}/assets/videos/{VIDEO_ID}/vod/{VIDEO_ID}.m3u8
 */

/**
 * Generate direct HLS URL for video preview
 * @param {string} videoId - The video _id from episode/movie data
 * @returns {string|null} Direct HLS stream URL
 */
export const getDirectVideoUrl = videoId => {
  if (!videoId) {
    console.warn('VideoUrlService: No video ID provided');
    return null;
  }

  // Validate environment variables
  if (!window.CONTENT_BASE_URL) {
    console.error('VideoUrlService: CONTENT_BASE_URL not set');
    return null;
  }

  if (!window.COMPANY_ID) {
    console.error('VideoUrlService: COMPANY_ID not set');
    return null;
  }

  // Validate video ID format (should be a valid MongoDB ObjectId or similar)
  if (typeof videoId !== 'string' || videoId.length < 10) {
    console.warn('VideoUrlService: Invalid video ID format:', videoId);
    return null;
  }

  // Ensure we're not accidentally using the video ID as company ID
  if (videoId === window.COMPANY_ID) {
    console.error('VideoUrlService: Video ID cannot be the same as Company ID:', videoId);
    return null;
  }

  const hlsUrl = `${window.CONTENT_BASE_URL}/files/company/${window.COMPANY_ID}/assets/videos/${videoId}/vod/${videoId}.m3u8`;

  return hlsUrl;
};

/**
 * Extract video ID from episode data
 * @param {Object} episode - Episode object from API
 * @returns {string|null} Video ID for HLS URL generation
 */
export const getEpisodeVideoId = episode => {
  if (!episode) {
    console.warn('❌ VideoUrlService: No episode data provided');
    return null;
  }

  // Try multiple possible locations for video ID
  let videoId = episode._id || episode.video?._id || episode.video_id;

  if (!videoId) {
    console.warn('❌ VideoUrlService: No video ID found in episode:', {
      title: episode.title,
      _id: episode._id,
      video: episode.video,
      video_id: episode.video_id,
      allKeys: Object.keys(episode),
    });
    return null;
  }

  return videoId;
};

/**
 * Extract video ID from single channel data
 * @param {Object} channel - Single channel object from API
 * @returns {string|null} Video ID for HLS URL generation
 */
export const getSingleChannelVideoId = channel => {
  if (!channel) {
    console.warn('❌ VideoUrlService: No channel data provided');
    return null;
  }

  // Try multiple possible locations for video ID in single channels
  let videoId =
    channel.video?._id || // Nested video object (most common for single channels)
    channel.video_id || // Direct video_id field
    channel._id; // Channel ID as fallback

  if (!videoId) {
    console.warn('❌ VideoUrlService: No video ID found in single channel:', {
      title: channel.title,
      _id: channel._id,
      video: channel.video,
      video_id: channel.video_id,
      allKeys: Object.keys(channel),
    });
    return null;
  }

  return videoId;
};

/**
 * Get preview URL for any content type (single channel or episode)
 * @param {Object} content - Content object (single channel or episode)
 * @param {string} contentType - 'single' or 'episode'
 * @returns {string|null} Direct HLS URL for preview
 */
export const getPreviewUrl = (content, contentType = 'single') => {
  if (!content) {
    console.warn('❌ VideoUrlService: No content provided for preview URL');
    return null;
  }

  let videoId;

  if (contentType === 'episode') {
    videoId = getEpisodeVideoId(content);
  } else {
    // contentType === 'single' or fallback
    videoId = getSingleChannelVideoId(content);
  }

  if (!videoId) {
    console.warn('❌ VideoUrlService: Could not extract video ID for preview:', {
      content: {
        _id: content._id,
        title: content.title,
        video: content.video,
        video_id: content.video_id,
      },
      contentType,
    });
    return null;
  }

  const directUrl = getDirectVideoUrl(videoId);

  return directUrl;
};

/**
 * Validate HLS URL accessibility
 * @param {string} hlsUrl - HLS URL to validate
 * @returns {Promise<boolean>} Whether URL is accessible
 */
export const validateHlsUrl = async hlsUrl => {
  if (!hlsUrl) {
    console.warn('VideoUrlService: No URL provided for validation');
    return false;
  }

  try {
    // Use a shorter timeout for validation
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

    const response = await fetch(hlsUrl, {
      method: 'HEAD',
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    const isValid = response.ok;

    if (!isValid) {
      console.warn('VideoUrlService: HLS URL not accessible:', {
        hlsUrl,
        status: response.status,
        statusText: response.statusText,
      });
    }

    return isValid;
  } catch (error) {
    if (error.name === 'AbortError') {
      console.warn('VideoUrlService: HLS URL validation timeout:', { hlsUrl });
    } else {
      console.warn('VideoUrlService: Error validating HLS URL:', {
        hlsUrl,
        error: error.message,
        type: error.name,
      });
    }
    return false;
  }
};

export default {
  getDirectVideoUrl,
  getEpisodeVideoId,
  getSingleChannelVideoId,
  getPreviewUrl,
  validateHlsUrl,
};
